import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Info } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => d.toISOString().slice(0, 10);
const CLASS_LABEL = { engaged: 'Engaged', low: 'Low Engagement', no_recorded_engagement: 'No Recorded Engagement' };
const CLASS_STYLE = {
  engaged: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  low: 'bg-amber-50 text-amber-800 border-amber-200',
  no_recorded_engagement: 'bg-stone-100 text-stone-600 border-stone-200',
};

const Stat = ({ label, value, hint, testid }) => (
  <Card className="p-3" data-testid={testid}>
    <p className="text-xs text-stone-500">{label}</p>
    <p className="text-2xl font-serif text-stone-900 mt-1">{value ?? '—'}</p>
    {hint && <p className="text-[11px] text-stone-400 mt-0.5">{hint}</p>}
  </Card>
);

const NumField = ({ label, value, onChange, testid }) => (
  <label className="text-xs text-stone-600 flex flex-col gap-1">
    {label}
    <Input type="number" min={1} max={90} value={value} onChange={(e) => onChange(e.target.value)} className="w-24 h-9" data-testid={testid} />
  </label>
);

function OverviewGrid({ o, since }) {
  const rate = o.delivery_rate == null ? '—' : `${o.delivery_rate}%`;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pfp-eng-overview">
      <Stat testid="pfp-eng-active-subs" label="Active PFP subscriptions" value={o.active_subscriptions} hint={`of ${o.total_pfp_parents} PFP parents`} />
      <Stat testid="pfp-eng-receiving" label="Parents receiving WhatsApp ideas" value={o.receiving_whatsapp_ideas} />
      <Stat testid="pfp-eng-sent" label="Daily ideas sent / failed" value={`${o.ideas_sent} / ${o.ideas_failed}`} />
      <Stat testid="pfp-eng-delivery-rate" label="WhatsApp delivery rate" value={rate}
        hint={o.delivery_tracked ? `${o.delivered} delivered · ${o.delivery_pending} awaiting confirmation · read (where reported): ${o.read_where_reported}` : `Delivery tracking ${since ? `since ${since}` : 'starts with the next send'}`} />
      <Stat testid="pfp-eng-wa" label="Parents engaging via WhatsApp" value={o.engaging_whatsapp} hint="replied / messaged the bot" />
      <Stat testid="pfp-eng-app" label="Parents engaging via the app" value={o.engaging_app} />
      <Stat testid="pfp-eng-tried" label="Parents reporting they tried ideas" value={o.reported_tried} hint="replied 1 or sent a LOG" />
      <Stat testid="pfp-eng-quiet" label="No recorded engagement (7 / 14 / 30d)"
        value={`${o.no_recorded_engagement_7d} / ${o.no_recorded_engagement_14d} / ${o.no_recorded_engagement_30d}`} hint="not the same as not using the ideas" />
    </div>
  );
}

function TrendChart({ trend }) {
  return (
    <Card className="p-4" data-testid="pfp-eng-trend">
      <p className="text-sm font-medium text-stone-800 mb-2">Engagement over time (parents per day)</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="engaged_parents" name="Any engagement" stroke="#7C6F9C" dot={false} />
            <Line type="monotone" dataKey="whatsapp_parents" name="WhatsApp" stroke="#059669" dot={false} />
            <Line type="monotone" dataKey="app_parents" name="App" stroke="#2563eb" dot={false} />
            <Line type="monotone" dataKey="tried_parents" name="Tried idea" stroke="#d97706" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function ParentTable({ rows }) {
  const [cls, setCls] = useState('');
  const shown = cls ? rows.filter((r) => r.classification === cls) : rows;
  return (
    <Card className="p-4 space-y-3" data-testid="pfp-eng-parents">
      <div className="flex flex-wrap gap-2">
        {['', 'engaged', 'low', 'no_recorded_engagement'].map((k) => (
          <button key={k || 'all'} onClick={() => setCls(k)} data-testid={`pfp-eng-class-filter-${k || 'all'}`}
            className={`px-3 py-1.5 text-xs rounded-full border ${cls === k ? 'bg-[#7C6F9C] text-white border-[#7C6F9C]' : 'bg-white border-stone-200 text-stone-700'}`}>
            {k ? CLASS_LABEL[k] : 'All'} ({k ? rows.filter((r) => r.classification === k).length : rows.length})
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-stone-500 text-left">
            <tr>{['Parent', 'Last app', 'Last WhatsApp', 'Last tried', 'Tried total', 'WA days/wk', 'Status'].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.email} className="border-t border-stone-100" data-testid="pfp-eng-parent-row">
                <td className="py-2 pr-3"><div className="text-stone-900">{r.name || '—'}</div><div className="text-xs text-stone-500">{r.email}</div></td>
                <td className="py-2 pr-3">{r.last_app_at || '—'}</td>
                <td className="py-2 pr-3">{r.last_whatsapp_at || '—'}</td>
                <td className="py-2 pr-3">{r.last_tried_at || '—'}</td>
                <td className="py-2 pr-3">{r.tried_total}</td>
                <td className="py-2 pr-3">{r.whatsapp_days_per_week}</td>
                <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full border text-xs ${CLASS_STYLE[r.classification]}`}>{CLASS_LABEL[r.classification]}</span></td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={7} className="py-6 text-center text-stone-400">No parents in this group</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export const PFPEngagementPanel = () => {
  const today = new Date();
  const [start, setStart] = useState(iso(new Date(today.getTime() - 29 * 864e5)));
  const [end, setEnd] = useState(iso(today));
  const [windowDays, setWindowDays] = useState(14);
  const [minDays, setMinDays] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const headers = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/pfp-analytics/engagement`, {
        ...headers, params: { start, end, window_days: windowDays, engaged_min_days: minDays },
      });
      setData(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not load engagement analytics');
    } finally { setLoading(false); }
  }, [headers, start, end, windowDays, minDays]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-4" data-testid="pfp-engagement-panel">
      <h2 className="text-lg font-serif text-stone-900">Engagement (WhatsApp + app)</h2>
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-stone-600 flex flex-col gap-1">From
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="pfp-eng-start" />
        </label>
        <label className="text-xs text-stone-600 flex flex-col gap-1">To
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="pfp-eng-end" />
        </label>
        <NumField label="Look-back days" value={windowDays} onChange={setWindowDays} testid="pfp-eng-window" />
        <NumField label="Engaged if active on ≥ days" value={minDays} onChange={setMinDays} testid="pfp-eng-min-days" />
        <Button onClick={load} disabled={loading} data-testid="pfp-eng-apply">
          {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Apply
        </Button>
      </Card>
      {error && <p className="text-sm text-red-600" data-testid="pfp-eng-error">{error}</p>}
      {data && (
        <>
          <p className="text-xs text-stone-500 flex gap-1.5 items-start" data-testid="pfp-eng-rules">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Engaged = active on {data.rules.engaged_min_days}+ of the last {data.rules.window_days} days; Low = 1+ day; otherwise No Recorded Engagement.
            Active = replied on WhatsApp, used the app, or confirmed trying an idea (each day counted once). Delivery alone is never counted as engagement,
            and parents may use ideas without telling us.
          </p>
          <OverviewGrid o={data.overview} since={data.delivery_tracking_since} />
          <TrendChart trend={data.trend} />
          <ParentTable rows={data.parents} />
        </>
      )}
    </section>
  );
};
